import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api.js';
import StatusBadge from '../components/StatusBadge.jsx';
import PageHeader from '../components/PageHeader.jsx';
import PageState from '../components/PageState.jsx';
import SectionCard from '../components/SectionCard.jsx';
import Notice from '../components/Notice.jsx';
import ConsoleTable from '../components/ConsoleTable.jsx';
import StatusWord from '../components/StatusWord.jsx';
import ApproveTenant from '../components/ApproveTenant.jsx';
import { AdminInviteFields, ClaimLinkResult, ReissueOnboardingLink } from '../components/OnboardingLink.jsx';
import { TIER_KEYS, tierLabel } from '../lib/tier-labels.js';
import { customerSectionPath } from '../lib/navigation.js';
import { MANUAL_CHANNELS, DEFAULT_MANUAL_CHANNEL, channelLabel } from '../../../shared/channel.mjs';
import { formatScore } from '../lib/score-format.js';
import { trustStackOutdated } from '../../../shared/trust-stack.mjs';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [approved, setApproved] = useState(null);
  const { me, startImpersonating } = useOutletContext();
  const isOperator = me?.user?.isOperator;
  // The cursor of the last request, so Retry repeats exactly the page that failed.
  const lastCursor = useRef(null);

  async function loadPage(nextCursor) {
    lastCursor.current = nextCursor;
    setLoading(true);
    setError(null);
    try {
      const page = await api.listCustomers(nextCursor);
      setCustomers((prev) => nextCursor ? [...prev, ...page.items] : page.items);
      setCursor(page.cursor);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const navigate = useNavigate();

  useEffect(() => { loadPage(null); }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        className="!mb-0"
        title="Customers"
        subline={`Every onGuard customer and where its deployment stands. ${customers.length} loaded.`}
        action={isOperator ? <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>Add customer</button> : null}
      />

      {showCreate && (
        <CreateCustomerForm
          onCreated={(c) => {
            setShowCreate(false);
            loadPage(null);
            navigate(customerSectionPath(c.customerId, 'overview'));
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {approved ? (
        <Notice tone="good" titleAs="p" role="status" title={`Tenant approved for ${approved}.`} testId="approve-tenant-done">
          Consent is confirmed. The list is refreshed.
        </Notice>
      ) : null}

      {error ? (
        <PageState state="error" title="The customer list could not be loaded" message={error} onRetry={() => loadPage(lastCursor.current)} />
      ) : null}

      {loading && customers.length === 0 && !error ? (
        <PageState state="loading" header={false} label="Loading customers" />
      ) : null}

      {!loading && !error && customers.length === 0 ? (
        <PageState
          state="empty"
          title="No customers yet"
          message="Customers appear here once they buy onGuard on AWS Marketplace or an operator adds one."
        />
      ) : null}

      {customers.length > 0 ? (
        // A click anywhere on a row opens the customer, like its name link (the link stays the keyboard path).
        // ConsoleTable owns the rows, so the click is delegated to the row's customer link.
        <div
          onClick={(e) => {
            if (e.target.closest('a, button, input, select, label')) return;
            const link = e.target.closest('tbody tr')?.querySelector('a[data-customer-link]');
            if (link) navigate(link.getAttribute('href'));
          }}
        >
          <ConsoleTable
            label="Customers"
            columns={[
              {
                id: 'customer',
                header: 'Customer',
                sortValue: (c) => c.companyName || c.customerId,
                className: 'cursor-pointer',
                cell: (c) => (
                  <>
                    <Link
                      to={customerSectionPath(c.customerId, 'overview')}
                      data-customer-link=""
                      className="font-medium text-ipc-primary hover:underline"
                    >
                      {c.companyName || c.customerId}
                    </Link>
                    <div className="font-mono text-xs text-ipc-muted">{c.customerId}</div>
                  </>
                ),
              },
              {
                id: 'tradingPartnerId',
                header: 'Trading Partner ID',
                sortValue: (c) => c.tradingPartnerId ?? null,
                cell: (c) => (c.tradingPartnerId
                  ? <span className="font-mono text-xs">{c.tradingPartnerId}</span>
                  : <span className="text-xs text-ipc-muted">not set</span>),
              },
              { id: 'channel', header: 'Channel', sortValue: (c) => (c.channel ? channelLabel(c.channel) : null), cell: (c) => (c.channel ? channelLabel(c.channel) : '-') },
              { id: 'tier', header: 'Tier', sortValue: (c) => (c.tierName ? tierLabel(c.tierName) : null), cell: (c) => (c.tierName ? tierLabel(c.tierName) : '-') },
              { id: 'score', header: 'onGuard score', sortValue: (c) => c.securityScore ?? null, cell: (c) => <ScoreBadge score={c.securityScore} /> },
              {
                id: 'status',
                header: 'Status',
                sortValue: (c) => c.status ?? null,
                cell: (c) => (
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={c.status} />
                    {/* Customer isolation: the customer's trust stack predates version 3 (the broad role). */}
                    {trustStackOutdated(c) === true && (
                      <StatusWord tone="warn" testId="trust-outdated" title="The customer's onGuard access role (trust stack) is an older version. The customer admin sees an update notice.">
                        Access role outdated
                      </StatusWord>
                    )}
                    <ApproveTenant
                      customer={c}
                      isOperator={isOperator}
                      className="btn-primary text-xs"
                      onApproved={(done) => { setApproved(done.companyName || done.customerId); loadPage(null); }}
                      onStale={() => loadPage(null)}
                    />
                    <ReissueOnboardingLink customer={c} isOperator={isOperator} className="btn-secondary text-xs" />
                  </div>
                ),
              },
              {
                id: 'lastRun',
                header: 'Last run',
                sortValue: (c) => (c.lastRunAt ? new Date(c.lastRunAt).getTime() : null),
                cell: (c) => (c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : '-'),
              },
              {
                id: 'viewAs',
                header: <span className="sr-only">View as</span>,
                cell: (c) => (
                  <button
                    type="button"
                    onClick={() => startImpersonating(c.customerId, c.companyName || c.customerId)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded text-ipc-muted transition-colors hover:bg-ipc-surface hover:text-ipc-primary"
                    title={`View as ${c.companyName || c.customerId}`}
                    aria-label={`View as ${c.companyName || c.customerId}`}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                ),
              },
            ]}
            rows={customers}
            getRowId={(c) => c.customerId}
            rowName={(c) => c.companyName || c.customerId}
            searchText={(c) => [c.companyName, c.customerId, c.tradingPartnerId].filter(Boolean).join(' ')}
            searchPlaceholder="Search customers"
            hasMore={!!cursor}
            onLoadMore={() => loadPage(cursor)}
            loadingMore={loading}
          />
        </div>
      ) : null}
    </div>
  );
}

// The score as ink text with a status dot (amber is never a text color).
function ScoreBadge({ score }) {
  if (score == null) return <span className="text-xs text-ipc-muted">-</span>;
  const tone = score >= 80 ? 'good' : score >= 60 ? 'warn' : 'bad';
  return (
    <StatusWord tone={tone} title={`onGuard score: ${formatScore(score)}`} aria-label={`onGuard score: ${formatScore(score)}`}>
      {formatScore(score)}
    </StatusWord>
  );
}

function CreateCustomerForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    customerId: '',
    companyName: '',
    entraTenantId: '',
    awsAccountId: '',
    tierName: 'professional',
    channel: DEFAULT_MANUAL_CHANNEL,
    notes: '',
    tradingPartnerId: '',
    adminEmail: '',
    sendInvite: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  // Auto-generate customerId slug from companyName
  function handleCompanyChange(e) {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);
    setForm((f) => ({
      ...f,
      companyName: name,
      customerId: f.customerId === '' || f.customerId === autoSlug(f.companyName) ? slug : f.customerId,
    }));
  }

  function autoSlug(name) {
    return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { tradingPartnerId, adminEmail, sendInvite, ...rest } = form;
      const normalizedTradingPartnerId = tradingPartnerId.trim().toUpperCase();
      const payload = normalizedTradingPartnerId
        ? { ...rest, tradingPartnerId: normalizedTradingPartnerId }
        : rest;
      const email = adminEmail.trim();
      if (email) Object.assign(payload, { adminEmail: email, sendInvite });
      const res = await api.createCustomer(payload);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <SectionCard title="Customer created" testId="customer-created">
        <p className="text-sm text-ipc-ink">
          <strong>{result.customer.companyName}</strong> ({result.customer.customerId}) is ready for onboarding.
        </p>
        {result.claimUrl ? <div className="mt-3"><ClaimLinkResult result={result} /></div> : null}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary" onClick={() => onCreated(result.customer)}>
            View customer
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>Close</button>
          <a href={result.onboardingUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-ipc-light hover:underline">
            Continue onboarding as operator
          </a>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="New internal customer"
      action={<button type="button" className="text-ipc-light hover:underline" onClick={onCancel}>Cancel</button>}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Company name <span className="text-red-400">*</span></label>
            <input className="input" value={form.companyName} onChange={handleCompanyChange}
                   placeholder="Acme Corp" required />
          </div>
          <div>
            <label className="label" htmlFor="customer-id">Customer stack prefix <span className="text-red-400">*</span></label>
            <input id="customer-id" aria-describedby="customer-id-help" className="input font-mono text-sm" value={form.customerId} onChange={set('customerId')}
                   placeholder="acme-corp" pattern="[a-z0-9][a-z0-9-]{2,30}[a-z0-9]" minLength={4} maxLength={32} required />
            <p id="customer-id-help" className="mt-1 text-xs text-ipc-muted">Prefixes the CloudFormation stack and the S3 resources in the customer AWS account. Lowercase letters, digits, and hyphens. 4 to 32 characters. Cannot be changed later. The business identifier is the Trading Partner ID.</p>
          </div>
          <div>
            <label className="label">Entra Tenant ID <span className="text-red-400">*</span></label>
            <input className="input font-mono text-sm" value={form.entraTenantId} onChange={set('entraTenantId')}
                   placeholder="8642927c-2cd0-49c5-ace3-db84e0e91f19" required />
          </div>
          <div>
            <label className="label">AWS Account ID <span className="text-red-400">*</span></label>
            <input className="input font-mono text-sm" value={form.awsAccountId} onChange={set('awsAccountId')}
                   placeholder="471394454770" pattern="[0-9]{12}" required />
          </div>
          <div>
            <label className="label">Tier</label>
            <select className="input" value={form.tierName} onChange={set('tierName')}>
              {TIER_KEYS.map((key) => (
                <option key={key} value={key}>{tierLabel(key)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="customer-channel">Channel</label>
            <select id="customer-channel" className="input" value={form.channel} onChange={set('channel')}>
              {MANUAL_CHANNELS.map((key) => (
                <option key={key} value={key}>{channelLabel(key)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Trading Partner ID</label>
            <input className="input font-mono text-sm" value={form.tradingPartnerId}
                   onChange={(e) => setForm((f) => ({ ...f, tradingPartnerId: e.target.value.toUpperCase() }))}
                   placeholder="ACMECORP" maxLength={32} />
            <p className="mt-1 text-xs text-ipc-muted">Uppercase letters and digits. Derived from the buyer's email domain for Marketplace customers; editable later.</p>
          </div>
        </div>
        <div>
          <label className="label">Notes</label>
          <input className="input" value={form.notes} onChange={set('notes')}
                 placeholder="e.g. 30-day pilot, POC for Acme security review" />
        </div>
        <AdminInviteFields
          idPrefix="create"
          email={form.adminEmail}
          onEmail={(v) => setForm((f) => ({ ...f, adminEmail: v }))}
          sendInvite={form.sendInvite}
          onSendInvite={(v) => setForm((f) => ({ ...f, sendInvite: v }))}
        />
        <p className="text-xs text-ipc-muted">
          Creating the customer issues a single-use onboarding link for its admin, shown on the next screen.
        </p>
        {error && <Notice tone="bad" title="The customer was not created" titleAs="h3">{error}</Notice>}
        <div className="flex gap-3">
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || !form.customerId || !form.entraTenantId || !form.awsAccountId}
          >
            {busy ? 'Creating...' : 'Create customer'}
          </button>
          <button type="button" className="btn-secondary text-sm" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </SectionCard>
  );
}
